import {
  selectActiveDownloads,
  updateDownload,
} from "./downloadSlice";

const DEFAULT_POLL_INTERVAL_MS = 1000;

function validateSyncInputs({ store, manager }) {
  if (
    !store ||
    typeof store.dispatch !== "function" ||
    typeof store.getState !== "function"
  ) {
    throw new Error(
      "A Redux store with dispatch and getState is required to sync download progress."
    );
  }

  if (!manager || typeof manager.getDownloadJob !== "function") {
    throw new Error(
      "A download manager with getDownloadJob is required to sync download progress."
    );
  }
}

function getActiveJobIds(store) {
  return selectActiveDownloads(store.getState()).map((job) => job.id);
}

export function startDownloadProgressSync({
  store,
  manager,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  setIntervalImpl = setInterval,
  clearIntervalImpl = clearInterval,
} = {}) {
  validateSyncInputs({ store, manager });

  let intervalId = null;
  let stopped = false;
  let pollInFlight = null;
  let unsubscribe = null;

  function stopInterval() {
    if (intervalId !== null) {
      clearIntervalImpl(intervalId);
      intervalId = null;
    }
  }

  function ensureInterval() {
    if (stopped || intervalId !== null || !getActiveJobIds(store).length) {
      return;
    }

    intervalId = setIntervalImpl(() => {
      return poll().catch((error) => {
        console.warn(error);
      });
    }, intervalMs);

    poll().catch((error) => {
      console.warn(error);
    });
  }

  function stop() {
    stopped = true;
    stopInterval();

    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  }

  async function poll() {
    if (stopped) {
      return [];
    }

    if (pollInFlight) {
      return pollInFlight;
    }

    pollInFlight = (async () => {
      const activeJobIds = getActiveJobIds(store);

      if (!activeJobIds.length) {
        stopInterval();
        return [];
      }

      const records = await Promise.all(
        activeJobIds.map((jobId) => manager.getDownloadJob({ jobId }))
      );
      const jobs = records
        .map((record) => record && record.job)
        .filter(Boolean);

      jobs.forEach((job) => {
        store.dispatch(updateDownload(job));
      });

      if (!getActiveJobIds(store).length) {
        stopInterval();
      }

      return jobs;
    })();

    try {
      return await pollInFlight;
    } finally {
      pollInFlight = null;
    }
  }

  if (typeof store.subscribe === "function") {
    unsubscribe = store.subscribe(() => {
      ensureInterval();
    });
  }

  ensureInterval();

  return {
    poll,
    stop,
    isRunning() {
      return intervalId !== null;
    },
    isStopped() {
      return stopped;
    },
  };
}
